/**
 * RELATED PRODUCTS / RECENTLY VIEWED: SP 横スクロールと pagination dots の同期補正
 *
 * 症状:
 *   最後のカードまでスクロールしても active dot が 1 つ手前で止まる。
 *
 * 原因:
 *   theme.js（ScrollCarousel）の現在位置算出は「scrollLeft に offsetLeft が最も近いセル」。
 *   本カルーセルは snap-start + 端の scrollLeft が maxScrollLeft（scrollWidth - clientWidth）で
 *   クランプされるため、最終カードの offsetLeft まで scrollLeft が到達できず、
 *   端では手前のカードが「最も近いセル」になってしまう。
 *
 * 方針（theme.js は共通 utility のため変更しない）:
 *   各カードの「実際に到達可能なスナップ位置」
 *     snapLeft(i) = clamp(カード左端の実描画位置 - scroll-padding-inline-start, 0, maxScrollLeft)
 *   を実測（getBoundingClientRect）から求め、現在の scrollLeft に最も近いカードを
 *   現在位置とする（同距離なら後ろのカード優先 = 端で最後の dot になる）。
 *   これは theme.js の select() が実際にスクロールする到達位置（calculateLeftScroll と
 *   同じクランプ）に対する正確な逆算であり、端の強制 active ではない。
 *
 * 適用範囲:
 *   scroll-carousel.collection-recently-viewed__carousel（RELATED PRODUCTS /
 *   RECENTLY VIEWED 共通クラス）のみ。dots（carousel-navigation）の aria-current を
 *   theme.js の更新後に上書き補正し、左矢印は端での内部 index 劣化による
 *   飛び戻りを防ぐため実到達スナップ位置ベースの 1 ポジション戻りに置き換える。
 *   theme.js 本体・他のカルーセル・PC グリッドには一切触れない。
 *   両セクションとも中身が fetch で後から差し込まれるため、
 *   MutationObserver で出現を監視してバインドする。
 *   ※ このストアは LTR 前提（scrollLeft は 0 以上）。
 */
(() => {
  'use strict';

  var CAROUSEL_SELECTOR = 'scroll-carousel.collection-recently-viewed__carousel';
  var CELL_SELECTOR = '.collection-recently-viewed__carousel-cell';
  var bound = new WeakSet();

  /* 各カードの「実際に到達可能なスナップ位置」（スクロール内容座標・端はクランプ）を実測する */
  function snapPositions(carousel) {
    var cells = Array.prototype.filter.call(
      carousel.querySelectorAll(CELL_SELECTOR),
      function (cell) { return !cell.hasAttribute('hidden'); }
    );
    var maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
    var scrollPadStart = parseInt(getComputedStyle(carousel).scrollPaddingInlineStart, 10) || 0;
    var baseLeft = carousel.getBoundingClientRect().left;
    var current = carousel.scrollLeft;
    return cells.map(function (cell) {
      /* カード左端のスクロール内容座標（transform 非使用前提の実描画位置） */
      var cellLeft = cell.getBoundingClientRect().left - baseLeft + current;
      /* このカードへスナップしたときに実際に到達する scrollLeft（端はクランプ） */
      return Math.min(Math.max(cellLeft - scrollPadStart, 0), maxScrollLeft);
    });
  }

  /* 現在表示中のカード index を実到達スナップ位置から算出する */
  function computeAccurateIndex(carousel) {
    var positions = snapPositions(carousel);
    if (positions.length === 0) {
      return -1;
    }
    var current = carousel.scrollLeft;
    var bestIndex = 0;
    var bestDistance = Infinity;
    positions.forEach(function (snapLeft, index) {
      var distance = Math.abs(snapLeft - current);
      /* 同距離は後ろのカード優先: 端でスナップ位置が重なった場合に最後の dot を選ぶ */
      if (distance <= bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    return bestIndex;
  }

  /* dots（carousel-navigation）の aria-current を正しい index へ合わせる */
  function syncDots(carousel) {
    /* SP スライダー非表示時（700px 以上は PC グリッド）は何もしない */
    if (carousel.clientWidth === 0 || !carousel.id) {
      return;
    }
    var nav = document.querySelector(
      'carousel-navigation[aria-controls="' + CSS.escape(carousel.id) + '"]'
    );
    if (!nav) {
      return;
    }
    var index = computeAccurateIndex(carousel);
    if (index < 0) {
      return;
    }
    var buttons = Array.prototype.filter.call(
      nav.querySelectorAll('button'),
      function (button) { return !button.hasAttribute('hidden'); }
    );
    buttons.forEach(function (button, buttonIndex) {
      var value = buttonIndex === index ? 'true' : 'false';
      if (button.getAttribute('aria-current') !== value) {
        button.setAttribute('aria-current', value);
      }
    });
  }

  /* rAF スロットル（theme.js の throttle と同方式） */
  function rafThrottle(callback) {
    var requestId = null;
    return function () {
      if (requestId === null) {
        requestId = requestAnimationFrame(function () {
          requestId = null;
          callback();
        });
      }
    };
  }

  function bind(carousel) {
    if (bound.has(carousel)) {
      return;
    }
    bound.add(carousel);
    var update = function () { syncDots(carousel); };
    /* carousel:change は theme.js（CarouselNavigation）のリスナーより後に登録される
       ため、theme.js が dots を更新した直後に同じディスパッチ内で補正できる */
    carousel.addEventListener('carousel:change', update);
    carousel.addEventListener('scrollend', update);
    carousel.addEventListener('scroll', rafThrottle(update));

    /* 左矢印: theme.js の previous() は内部 index 基準のため、端では内部 index が
       手前のカードへ劣化していて 2〜3 ポジション飛んで戻ることがある。
       capture で theme.js のリスナーより先にクリックを受けて伝播を止め、
       「現在位置より手前で最も近い実到達スナップ位置」へ 1 ポジションだけ戻す。
       （右矢印は theme.js の挙動で 1 ポジションずつ進み端にも正しく到達するため触らない） */
    if (carousel.id) {
      var prevWrap = document.querySelector(
        'carousel-prev-button[aria-controls="' + CSS.escape(carousel.id) + '"]'
      );
      if (prevWrap) {
        prevWrap.addEventListener('click', function (event) {
          event.stopPropagation();
          var current = carousel.scrollLeft;
          var target = -1;
          snapPositions(carousel).forEach(function (snapLeft) {
            /* 現在位置より 1px 以上手前のスナップ位置のうち最大のもの */
            if (snapLeft < current - 1 && snapLeft > target) {
              target = snapLeft;
            }
          });
          if (target >= 0) {
            carousel.scrollTo({ left: target, behavior: 'smooth' });
          }
        }, true);
      }
    }
  }

  function scan(root) {
    if (root.matches && root.matches(CAROUSEL_SELECTOR)) {
      bind(root);
    }
    if (root.querySelectorAll) {
      root.querySelectorAll(CAROUSEL_SELECTOR).forEach(bind);
    }
  }

  function start() {
    scan(document.body);
    /* RELATED（product-recommendations）/ RECENTLY VIEWED（recently-viewed-products）
       とも fetch 後に innerHTML 差し込みされるため、出現を監視してバインドする */
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            scan(node);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();